import React, { useState, useEffect } from 'react';
import { Box, Button, IconButton, Typography, Slide, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import LionIcon from './LionIcon';
import { trackEvent } from '../utils/gtm';
import { useLanguage } from '../context/LanguageContext';

// Routes that must stay 100% popup-free (paid-traffic landing pages, embeds,
// payment confirmation screens, …). Anything served from these paths skips the
// install banner entirely, no matter the device or dismissed state.
const POPUP_FREE_ROUTES = ['/start', '/lp/', '/landing/', '/ads/'];

function isPopupFreeRoute(pathname) {
    if (!pathname) return false;
    const p = String(pathname).toLowerCase();
    return POPUP_FREE_ROUTES.some(r => p === r || p.startsWith(r));
}

const PWAInstallBanner = () => {
    const { t } = useLanguage();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showBanner, setShowBanner] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [showIOSInstructions, setShowIOSInstructions] = useState(false);

    useEffect(() => {
        // Hard-suppress on dedicated landing routes that must convert without
        // any popup interrupting the funnel (paid Google Ads traffic, etc).
        if (isPopupFreeRoute(window.location?.pathname)) return;

        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches) return;
        if (window.navigator.standalone) return;

        // Check if dismissed recently (24h)
        const dismissed = localStorage.getItem('emailLion_pwaDismissed');
        if (dismissed && Date.now() - parseInt(dismissed) < 24 * 60 * 60 * 1000) return;

        // iOS detection
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(isIOSDevice);

        if (isIOSDevice) {
            // iOS doesn't support beforeinstallprompt, show manual instructions
            setTimeout(() => setShowBanner(true), 3000);
        }

        // Android / Chrome install prompt
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setTimeout(() => setShowBanner(true), 1500);
        };

        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstall = async () => {
        trackEvent('pwa_install_click', { platform: isIOS ? 'ios' : 'android_chrome' });

        if (isIOS) {
            setShowIOSInstructions(true);
            return;
        }
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            trackEvent('pwa_install_accepted', { platform: 'android_chrome' });
            setShowBanner(false);
        } else {
            trackEvent('pwa_install_dismissed', { platform: 'android_chrome' });
        }
        setDeferredPrompt(null);
    };

    const handleDismiss = () => {
        setShowBanner(false);
        setShowIOSInstructions(false);
        localStorage.setItem('emailLion_pwaDismissed', String(Date.now()));
    };

    if (!showBanner) return null;

    return (
        <Slide direction="up" in={showBanner}>
            <Paper
                elevation={8}
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1300,
                    borderRadius: '16px 16px 0 0',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                }}
            >
                <Box sx={{
                    p: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                    background: 'linear-gradient(135deg, rgba(21,101,192,0.05) 0%, rgba(100,181,246,0.05) 100%)',
                }}>
                    {/* Close button */}
                    <IconButton
                        size="small"
                        onClick={handleDismiss}
                        sx={{ position: 'absolute', top: 8, left: 8, color: 'text.secondary' }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>

                    {/* App icon & name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 52, height: 52, borderRadius: 2,
                            overflow: 'hidden', flexShrink: 0, lineHeight: 0, boxShadow: 2,
                        }}>
                            <LionIcon sx={{ fontSize: 52 }} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold">Email Lion</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('pwa.installSubtitle')}
                            </Typography>
                        </Box>
                    </Box>

                    {showIOSInstructions ? (
                        /* iOS Instructions */
                        <Box sx={{ textAlign: 'center', px: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('pwa.iosHowTo')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                <Typography variant="body2">
                                    {t('pwa.iosStep1')} <Box component="span" sx={{ fontSize: '1.2em' }}>⬆️</Box>
                                </Typography>
                                <Typography variant="body2">
                                    {t('pwa.iosStep2')} <Box component="span" sx={{ fontSize: '1.2em' }}>➕</Box>
                                </Typography>
                                <Typography variant="body2">
                                    {t('pwa.iosStep3')}
                                </Typography>
                            </Box>
                            <Button size="small" onClick={handleDismiss} sx={{ mt: 1.5 }}>{t('pwa.gotIt')}</Button>
                        </Box>
                    ) : (
                        /* Install buttons */
                        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                startIcon={<GetAppIcon />}
                                onClick={handleInstall}
                                sx={{ borderRadius: 2, py: 1.2, fontWeight: 'bold', fontSize: '0.95rem' }}
                            >
                                {isIOS ? t('pwa.installInstructions') : t('pwa.installApp')}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleDismiss}
                                sx={{ borderRadius: 2, minWidth: 90 }}
                            >
                                {t('pwa.notNow')}
                            </Button>
                        </Box>
                    )}

                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                        {t('pwa.noStoreNeeded')}
                    </Typography>
                </Box>
            </Paper>
        </Slide>
    );
};

export default PWAInstallBanner;
